import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PolicyGetMetadataResponse,
  PolicyGetRequestParams,
  PolicyGetResponse,
  PolicySearchResponse,
  PolicyTransactionPatchRequest,
  PolicyTransactionPostRequest,
  PolicyTransactionPostResponse,
  PolicyTransactionSearchRequestQuery,
  PolicyTransactionSearchResponse,
  BillingPlanSearchRequestQuery,
  BillingPlanSearchResponse,
  PolicySearchRequestQuery,
} from '@lio/api-types';
import { useClient } from '@utils/api-client';
import { PolicyTransactionStatus } from '@models/policyTransaction';
import { documentQueryKey } from '../document/keys';
import { buildQueryParamForArray } from '@utils/buildQueryParamForArray';
import { ooseResolutionQueryKeys } from './useGetOoseResolution';

export type PolicyTransactionItem = PolicyTransactionSearchResponse['items'][number];

export const useGetPolicy = ({ policyId }: Partial<PolicyGetRequestParams>) => {
  const client = useClient();
  const result = useQuery<PolicyGetResponse>({
    queryKey: ['policy', { policyId }],
    queryFn: () => client.get(`policy/${policyId}`).json<PolicyGetResponse>(),
    enabled: policyId !== undefined && policyId !== '',
    refetchOnWindowFocus: false,
    gcTime: 0,
    staleTime: 0,
  });
  return { ...result, policy: result.data };
};

const getPolicyTransactionsQuery = (params: PolicyTransactionSearchRequestQuery) => {
  const { quoteId, pageLimit, pageOffset, policyId, transactionType, createdByUserId, status, sort, includeCanceled } =
    params;
  const query = new URLSearchParams();
  if (quoteId) {
    query.append('quoteId', quoteId);
  }
  if (pageLimit) {
    query.append('pageLimit', String(pageLimit));
  }
  if (pageOffset) {
    query.append('pageOffset', String(pageOffset));
  }
  if (transactionType) {
    query.append('transactionType', transactionType);
  }
  if (createdByUserId) {
    query.append('createdByUserId', createdByUserId);
  }
  if (status) {
    buildQueryParamForArray(query, 'status', status);
  }
  if (policyId) {
    query.append('policyId', policyId);
  }
  if (sort) {
    query.append('sort', sort);
  } else {
    // maintain default behavior
    query.append('sort', '-effectiveOn,-createdAt');
  }
  if (includeCanceled !== undefined) {
    query.append('includeCanceled', includeCanceled.toString());
  }
  return query.toString();
};

const transactionsQueryKey = {
  get: ['policyTransaction'] as const,
  getWithParams: (params: PolicyTransactionSearchRequestQuery) => [...transactionsQueryKey.get, params] as const,
  search: ['policyTransactions'] as const,
  searchWithParams: (params: PolicyTransactionSearchRequestQuery) => [...transactionsQueryKey.search, params] as const,
};

export const useSearchPolicyTransactions = (params: PolicyTransactionSearchRequestQuery, enabled: boolean = true) => {
  const client = useClient();
  const query = getPolicyTransactionsQuery(params);
  const result = useQuery({
    queryKey: transactionsQueryKey.searchWithParams(params),
    queryFn: () => client.get(`policy-transaction?${query}`).json<PolicyTransactionSearchResponse>(),
    refetchOnWindowFocus: false,
    enabled: !!params.policyId && enabled,
  });
  return { ...result, policyTransactions: result.data };
};

const config = {
  staleTime: 60000,
};

// TODO remove offeringId once types are updated
// Exported for unit testing of query-param serialization.
export const buildPolicySearchUrl = (params: PolicySearchRequestQuery) => {
  const {
    pageOffset,
    pageLimit,
    sort,
    searchText,
    expiresOnMax,
    updatedAtMax,
    updatedAtMin,
    renewalStatus,
    status,
    insuranceProductId,
    productOptionId,
    productLabelId,
    writingCompanyId,
    effectiveOnMin,
    effectiveOnMax,
    underwriterId,
    brokerageId,
    brokerageProducerId,
    offeringId,
    hasOpenSubjectivities,
    borTransferStatus,
    isEligibleForBORTransfer,
    packageLabel,
    reportingQuoteSource,
  } = params;

  const queryParams = new URLSearchParams();

  if (pageLimit) {
    queryParams.append('pageLimit', pageLimit?.toString());
  }
  if (pageOffset) {
    queryParams.append('pageOffset', pageOffset?.toString());
  }

  if (sort) {
    queryParams.append('sort', sort?.toString());
  }

  if (searchText) {
    queryParams.append('searchText', searchText);
  }
  if (status?.length) {
    buildQueryParamForArray(queryParams, 'status', status);
  }
  if (renewalStatus) {
    queryParams.append('renewalStatus', renewalStatus);
  }

  if (expiresOnMax) {
    const expiresOnMaxDate = new Date(expiresOnMax);
    queryParams.append('expiresOnMax', expiresOnMaxDate.toISOString());
  }

  // TODO THE REST if we ever add more filters
  if (insuranceProductId) {
    queryParams.append('insuranceProductId', insuranceProductId);
  }

  if (updatedAtMax) {
    const updatedAtMaxDate = new Date(updatedAtMax);
    queryParams.append('updatedAtMax', updatedAtMaxDate.toISOString());
  }

  if (updatedAtMin) {
    const updatedAtMinDate = new Date(updatedAtMin);
    queryParams.append('updatedAtMin', updatedAtMinDate.toISOString());
  }

  if (productOptionId) {
    buildQueryParamForArray(queryParams, 'productOptionId', productOptionId);
  }

  if (productLabelId) {
    buildQueryParamForArray(queryParams, 'productLabelId', productLabelId);
  }

  if (writingCompanyId) {
    queryParams.append('writingCompanyId', writingCompanyId);
  }

  if (effectiveOnMax) {
    const effectiveOnMaxDate = new Date(effectiveOnMax);
    queryParams.append('effectiveOnMax', effectiveOnMaxDate.toISOString());
  }

  if (effectiveOnMin) {
    const effectiveOnMinDate = new Date(effectiveOnMin);
    queryParams.append('effectiveOnMin', effectiveOnMinDate.toISOString());
  }

  if (underwriterId) {
    buildQueryParamForArray(queryParams, 'underwriterId', underwriterId);
  }

  if (brokerageId) {
    buildQueryParamForArray(queryParams, 'brokerageId', brokerageId);
  }

  if (brokerageProducerId) {
    buildQueryParamForArray(queryParams, 'brokerageProducerId', brokerageProducerId);
  }

  if (offeringId) {
    buildQueryParamForArray(queryParams, 'offeringId', offeringId);
  }

  if (hasOpenSubjectivities) {
    queryParams.append('hasOpenSubjectivities', 'true');
  }

  if (borTransferStatus) {
    queryParams.append('borTransferStatus', borTransferStatus);
  }

  if (isEligibleForBORTransfer) {
    queryParams.append('isEligibleForBORTransfer', 'true');
  }

  if (packageLabel) {
    buildQueryParamForArray(queryParams, 'packageLabel', packageLabel);
  }

  if (reportingQuoteSource) {
    buildQueryParamForArray(queryParams, 'reportingQuoteSource', reportingQuoteSource);
  }

  return queryParams;
};

export const policySearchQueryKeys = {
  search: ['policies'] as const,
  searchWithParams: (params: PolicySearchRequestQuery) => [...policySearchQueryKeys.search, params] as const,
};

export const usePolicySearch = (params: PolicySearchRequestQuery, enabled: boolean) => {
  const client = useClient();
  const queryParams = buildPolicySearchUrl(params as any);

  return useQuery<PolicySearchResponse, Error>({
    queryKey: policySearchQueryKeys.searchWithParams(params),
    queryFn: () => client.get(`policy?${queryParams}`).json<PolicySearchResponse>(),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled: enabled !== false,
    ...config,
  });
};

export const useCreatePolicyTransaction = () => {
  const queryClient = useQueryClient();
  const client = useClient();

  const result = useMutation({
    mutationFn: (body: PolicyTransactionPostRequest) =>
      client
        .post('policy-transaction', {
          json: {
            ...body,
          },
        })
        .json<PolicyTransactionPostResponse>(),
    onSuccess: async (res) => {
      queryClient.invalidateQueries({ queryKey: transactionsQueryKey.search });
      queryClient.invalidateQueries({ queryKey: ['policy'] });
      queryClient.invalidateQueries({ queryKey: ['quote'] });
      // A newly created transaction can also create an OoSE resolution. Wait
      // for any cached "no active resolution" result to be refreshed before
      // callers navigate to the quote and choose its bind action.
      await queryClient.invalidateQueries({ queryKey: ooseResolutionQueryKeys.get(res.policyId) });
      /* Allowing policy transaction to progress before fetching documents
      so the new document is already created before the new search */
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: documentQueryKey.searchWithParams({
            policyId: res.policyId,
          }),
        });
      }, 2000);
    },
  });
  return { ...result, createPolicyTransaction: result.mutateAsync };
};

export const usePolicyMetadata = (customerId?: string) => {
  const client = useClient();
  const queryParams = new URLSearchParams();
  if (customerId) {
    queryParams.append('customerId', customerId);
  }
  const result = useQuery<PolicyGetMetadataResponse, Error>({
    queryKey: ['policyMetadata', { customerId }],
    queryFn: () => client.get(`policy/metadata?${queryParams}`).json<PolicyGetMetadataResponse>(),
    ...config,
  });
  return result;
};

export const useUpdatePolicyTransaction = () => {
  const queryClient = useQueryClient();
  const client = useClient();
  const result = useMutation({
    mutationFn: (
      request: { body: PolicyTransactionPatchRequest } & {
        policyTransactionId: string;
      },
    ) =>
      client
        .patch(`policy-transaction/${request.policyTransactionId}`, {
          json: {
            ...request.body,
          },
        })
        .json<PolicyTransactionPostResponse>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transactionsQueryKey.search });
      queryClient.invalidateQueries({ queryKey: ['quoteDiff'] });
      queryClient.invalidateQueries({ queryKey: ['policy'] });
      queryClient.invalidateQueries({ queryKey: ['quote'] });
    },
  });
  return { ...result, updatePolicyTransaction: result.mutateAsync };
};

export const useDeletePolicyTransaction = () => {
  const queryClient = useQueryClient();
  const client = useClient();
  const result = useMutation({
    mutationFn: (policyTransactionId: string) =>
      client.patch(`policy-transaction/${policyTransactionId}`, {
        json: { status: PolicyTransactionStatus.Canceled },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transactionsQueryKey.search });
      queryClient.invalidateQueries({ queryKey: ['policy'] });
      queryClient.invalidateQueries({ queryKey: ['quote'] });
    },
  });
  return { ...result, deletePolicyTransaction: result.mutateAsync };
};

export const useDisassociateConditionalRenewalQuoteId = () => {
  const queryClient = useQueryClient();
  const client = useClient();
  const result = useMutation({
    mutationFn: (policyTransactionId: string) =>
      client.post(`policy-transaction/${policyTransactionId}/renewal-quote-disassociation`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transactionsQueryKey.search });
      queryClient.invalidateQueries({ queryKey: ['policy'] });
      queryClient.invalidateQueries({ queryKey: ['quote'] });
    },
  });
  return {
    ...result,
    disassociateConditionalRenewalQuoteId: result.mutateAsync,
  };
};

const billingPlanQueryKey = {
  search: ['brokerageCustomer'] as const,
  searchWithParams: (params: BillingPlanSearchRequestQuery) => [...billingPlanQueryKey.search, params] as const,
};

export const useSearchBillingPlan = (params: BillingPlanSearchRequestQuery) => {
  const client = useClient();
  const queryParams = new URLSearchParams();

  if (params.policyTermType) {
    queryParams.append('policyTermType', params.policyTermType);
  }

  if (params.pageOffset) {
    queryParams.append('pageOffset', String(params.pageOffset));
  }

  if (params.pageLimit) {
    queryParams.append('pageLimit', String(params.pageLimit));
  }

  const result = useQuery<BillingPlanSearchResponse, Error>({
    queryKey: billingPlanQueryKey.searchWithParams(params),
    queryFn: () => client.get(`billing-plan?${queryParams}`).json<BillingPlanSearchResponse>(),
    placeholderData: keepPreviousData,
    ...config,
  });

  return { ...result, billingPlans: result.data };
};
